import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, insuranceOperationsTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceQuoteListItem } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-quote-workbench',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    FormsModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.quotes.title')"
      [description]="t('insurance.quotes.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.quotes.help.page.title'"
        [descriptionKey]="'insurance.quotes.help.page.description'"
        [bulletKeys]="[
          'insurance.quotes.help.page.bullets.scope',
          'insurance.quotes.help.page.bullets.live',
          'insurance.quotes.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.quotes.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.quotes.help.listFilters.title'"
                [descriptionKey]="'insurance.quotes.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.quotes.help.listFilters.bullets.search',
                  'insurance.quotes.help.listFilters.bullets.status',
                  'insurance.quotes.help.listFilters.bullets.dates'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.quotes.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.quotes.filters.status') }}</span>
                <p-select
                  [options]="quoteStatusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.quotes.filters.fromDate') }}</span>
                <ain-date-input [(ngModel)]="fromDateFilter" name="fromDateFilter" />
              </label>
              <label>
                <span>{{ t('insurance.quotes.filters.toDate') }}</span>
                <ain-date-input [(ngModel)]="toDateFilter" name="toDateFilter" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.quotes.states.empty')" [message]="t('insurance.quotes.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.quotes.fields.quoteNumber') }}</th>
                  <th>{{ t('insurance.quotes.fields.status') }}</th>
                  <th>{{ t('insurance.quotes.fields.validUntil') }}</th>
                  <th>{{ t('insurance.quotes.fields.pricingHash') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.quoteNumber }}</button></td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.validUntilUtc | ainDate }}</td>
                  <td>{{ row.pricingSnapshotHash || '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (info()) { <p class="info">{{ info() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .live-status { margin-inline-start: auto; font-size: 12px; color: #4b5563; }
    .info { color: #1d4ed8; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceQuoteWorkbenchComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveTopic: string | null = null;
  private liveEventsSubscription: Subscription | null = null;
  readonly loading = signal(true);
  readonly info = signal('');
  readonly rows = signal<InsuranceQuoteListItem[]>([]);
  readonly selectedRows = signal<InsuranceQuoteListItem[]>([]);
  readonly search = signal('');
  statusFilter: string | null = null;
  fromDateFilter: string | null = null;
  toDateFilter: string | null = null;

  readonly quoteStatusOptions = [
    { value: 'Draft', labelKey: 'insurance.quotes.status.draft' },
    { value: 'Priced', labelKey: 'insurance.quotes.status.priced' },
    { value: 'Expired', labelKey: 'insurance.quotes.status.expired' },
    { value: 'ConvertedToProposal', labelKey: 'insurance.quotes.status.convertedToProposal' },
    { value: 'Cancelled', labelKey: 'insurance.quotes.status.cancelled' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    if (this.liveTopic) {
      void this.liveUpdateService.unsubscribe(this.liveTopic);
      this.liveTopic = null;
    }
  }

  load(): void {
    this.loading.set(true);
    this.api.listQuotes().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.info.set('');
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceQuoteListItem[] {
    const value = this.search().trim().toLowerCase();
    return this.rows()
      .filter(row => !value
        || row.quoteNumber.toLowerCase().includes(value)
        || row.status.toLowerCase().includes(value))
      .filter(row => !this.statusFilter || row.status === this.statusFilter)
      .filter(row => !this.fromDateFilter || new Date(row.validUntilUtc) >= new Date(this.fromDateFilter))
      .filter(row => !this.toDateFilter || new Date(row.validUntilUtc) <= new Date(this.toDateFilter));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsuranceQuoteListItem[] | InsuranceQuoteListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/quotes', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/quotes/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    const header = ['QuoteNumber', 'Status', 'ValidUntilUtc', 'PricingSnapshotHash'];
    const lines = rows.map(item =>
      [item.quoteNumber, item.status, item.validUntilUtc, item.pricingSnapshotHash ?? '']
        .map(value => `"${value.replace(/"/g, '""')}"`)
        .join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'insurance-quotes.csv';
    anchor.click();
    URL.revokeObjectURL(url);
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  statusKey(status: InsuranceQuoteListItem['status']): string {
    return `insurance.quotes.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  liveStatusTranslationKey(state: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'): string {
    return liveStatusTranslationKey(state);
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = insuranceOperationsTopic(tenantId);
    this.liveTopic = topic;
    await this.liveUpdateService.subscribe(topic);
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.insuranceQuotesUpdated))
      .subscribe(() => this.load());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
